/**
 * Headcount-weighted REPS rollups (Beta-1 plan P2-38, ⚑Q-23 option B, TICK A40/C40). Pure module.
 *
 *   NWT (team)   score = plain mean of its responses
 *   Department   score = headcount-weighted mean of its NWT scores (Tim 26 Oct: crews too —
 *                the Beta-1 "crews weigh equally" rule was dropped at his request)
 *   Site         score = headcount-weighted mean of its department scores
 *   Region / All-Sites = headcount-weighted mean of site scores
 *
 * Headcount = members in the org's `membersIndex` scoped to that unit (team members roll up into
 * their department and site). Headcount weighting is used at a level only when EVERY scoring child
 * at that level has a headcount; otherwise that level weighs by response count (a half-loaded
 * people list — a few testers with scopes — must not make one team weigh 1 and its neighbour 40).
 * An org that has not loaded its people at all therefore rolls up response-weighted, as before.
 * Responses that name a unit their parent does not know (e.g. a site-only response) are folded in
 * as a "direct" child of that parent, weighted by their count. Every node carries
 * `responseCount`, `teamsCount`, `headcount` and `lowConfidence` (responses < 5 or, when the
 * headcount is known, response rate < 40 %) for the "based on N responses from M teams" line.
 */
import { calculateResponseREPS } from './scoring.js';

export const QUADRANTS = Object.freeze(['reliability', 'efficiency', 'productivity', 'safety']);
export const LOW_CONFIDENCE_MIN_RESPONSES = 5;
export const LOW_CONFIDENCE_MIN_RATE = 0.4;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const zero = () => ({ reliability: 0, efficiency: 0, productivity: 0, safety: 0 });

/** Operating crew? Explicit `nwtType` wins; legacy data falls back to the name ("Shift Crew A"). */
export function isCrew(team) {
  if (!team) return false;
  if (team.nwtType) return team.nwtType === 'crew';
  return /\b(crew|shift)\b/i.test(team.name || '');
}

/** Count `membersIndex` entries per scope: { team: {id: n}, department: {id: n}, site: {id: n} }. */
export function headcountsFromMembers(org) {
  const out = { team: {}, department: {}, site: {}, organization: 0 };
  const index = org?.membersIndex || {};
  for (const m of Object.values(index)) {
    if (!m) continue;
    const type = m.scopeType;
    if (type === 'team' || type === 'department' || type === 'site') {
      if (m.scopeId) out[type][m.scopeId] = (out[type][m.scopeId] || 0) + 1;
    }
    out.organization += 1;
  }
  return out;
}

function meanOfResponses(list) {
  if (!list.length) return null;
  const sum = zero();
  for (const r of list) {
    const s = r.repsScores || calculateResponseREPS(r.answers || {});
    for (const q of QUADRANTS) sum[q] += Number(s?.[q]) || 0;
  }
  const scores = {};
  for (const q of QUADRANTS) scores[q] = round2(sum[q] / list.length);
  return scores;
}

function weightedMean(children) {
  const withScores = children.filter((c) => c && c.scores && c.weight > 0);
  if (!withScores.length) return null;
  const total = withScores.reduce((a, c) => a + c.weight, 0);
  const scores = {};
  for (const q of QUADRANTS) scores[q] = round2(withScores.reduce((a, c) => a + c.scores[q] * c.weight, 0) / total);
  return scores;
}

/** Weight scoring children by headcount when every one of them has it, else by response count. */
function weighChildren(children, direct = null) {
  const scoring = children.filter((c) => c.scores);
  const useHc = scoring.length > 0 && scoring.every((c) => c.headcount > 0) && (!direct || direct.headcount > 0);
  const out = children.map((c) => ({ ...c, weight: c.scores ? (useHc ? c.headcount : c.responseCount) : 0 }));
  if (direct) out.push({ ...direct, weight: useHc ? direct.headcount : direct.responseCount });
  return out;
}

function lowConfidence(responseCount, headcount) {
  if (responseCount < LOW_CONFIDENCE_MIN_RESPONSES) return true;
  if (headcount > 0 && responseCount / headcount < LOW_CONFIDENCE_MIN_RATE) return true;
  return false;
}

function finishNode(node) {
  node.overall = node.scores ? round2(QUADRANTS.reduce((a, q) => a + node.scores[q], 0) / 4) : null;
  node.lowConfidence = node.scores ? lowConfidence(node.responseCount, node.headcount) : true;
  node.basis = describeBasis(node);
  return node;
}

/** "based on 42 responses from 6 teams" */
export function describeBasis(node) {
  if (!node || !node.responseCount) return 'no responses yet';
  const n = node.responseCount;
  const m = node.teamsCount || 0;
  const teams = m > 0 ? ` from ${m} ${m === 1 ? 'team' : 'teams'}` : '';
  return `based on ${n} ${n === 1 ? 'response' : 'responses'}${teams}`;
}

/**
 * Roll the last-N-days responses up through the org structure.
 * @param {Object} org        Hugo_Organizations doc ({ sites, departments, teams, membersIndex })
 * @param {Array}  responses  responses with siteId / departmentId / teamId (+ answers or repsScores)
 * @returns {{ teams, departments, sites, regions, enterprise }} keyed by id (enterprise is one node)
 */
export function rollupScores(org, responses = []) {
  const sites = org?.sites || [];
  const departments = org?.departments || [];
  const teams = org?.teams || [];
  const hc = headcountsFromMembers(org);

  const byTeam = {};
  const byDept = {};
  const bySite = {};
  const unassigned = [];
  const knownTeam = new Set(teams.map((t) => t.id));
  const knownDept = new Set(departments.map((d) => d.id));
  const knownSite = new Set(sites.map((s) => s.id));
  for (const r of responses || []) {
    if (r.teamId && knownTeam.has(r.teamId)) (byTeam[r.teamId] = byTeam[r.teamId] || []).push(r);
    else if (r.departmentId && knownDept.has(r.departmentId)) (byDept[r.departmentId] = byDept[r.departmentId] || []).push(r);
    else if (r.siteId && knownSite.has(r.siteId)) (bySite[r.siteId] = bySite[r.siteId] || []).push(r);
    else unassigned.push(r);
  }

  // Teams
  const teamNodes = {};
  for (const t of teams) {
    const list = byTeam[t.id] || [];
    const headcount = hc.team[t.id] || 0;
    teamNodes[t.id] = finishNode({
      id: t.id, type: 'team', name: t.name, departmentId: t.departmentId, crew: isCrew(t),
      scores: meanOfResponses(list), responseCount: list.length, teamsCount: list.length ? 1 : 0, headcount,
    });
  }

  // Departments
  const deptNodes = {};
  for (const d of departments) {
    const children = teams.filter((t) => t.departmentId === d.id).map((t) => teamNodes[t.id]);
    const direct = byDept[d.id] || [];
    const directHc = hc.department[d.id] || 0;
    const headcount = children.reduce((a, c) => a + c.headcount, 0) + directHc;
    const scoring = children.filter((c) => c.scores);
    const useHc = scoring.length > 0 && scoring.every((c) => c.headcount > 0) && (!direct.length || directHc > 0);
    const sizeOf = (c) => (useHc ? c.headcount : c.responseCount);
    // Tim 26 Oct (A40 / Q-23): every NWT, crews included, weighs its headcount. The Beta-1 "crews
    // equal" rule is gone — "cleaner if crews, NWTs, etc. are all weighted by head count".
    const weighted = children.map((c) => ({ ...c, weight: c.scores ? sizeOf(c) : 0 }));
    if (direct.length) weighted.push({ scores: meanOfResponses(direct), weight: useHc ? directHc : direct.length, responseCount: direct.length });
    const responseCount = children.reduce((a, c) => a + c.responseCount, 0) + direct.length;
    deptNodes[d.id] = finishNode({
      id: d.id, type: 'department', name: d.name, siteId: d.siteId,
      scores: weightedMean(weighted), responseCount,
      teamsCount: children.filter((c) => c.scores).length + (direct.length ? 1 : 0), headcount,
    });
  }

  // Sites
  const siteNodes = {};
  for (const s of sites) {
    const children = departments.filter((d) => d.siteId === s.id).map((d) => deptNodes[d.id]);
    const direct = bySite[s.id] || [];
    const directHc = hc.site[s.id] || 0;
    const headcount = children.reduce((a, c) => a + c.headcount, 0) + directHc;
    const weighted = weighChildren(children, direct.length ? { scores: meanOfResponses(direct), headcount: directHc, responseCount: direct.length } : null);
    siteNodes[s.id] = finishNode({
      id: s.id, type: 'site', name: s.name, region: s.region || null,
      scores: weightedMean(weighted),
      responseCount: children.reduce((a, c) => a + c.responseCount, 0) + direct.length,
      teamsCount: children.reduce((a, c) => a + c.teamsCount, 0) + (direct.length ? 1 : 0), headcount,
    });
  }

  const siteList = Object.values(siteNodes);
  const rollSites = (list, extra = []) => {
    const weighted = weighChildren(list).concat(extra);
    return {
      scores: weightedMean(weighted),
      responseCount: list.reduce((a, c) => a + c.responseCount, 0) + extra.reduce((a, c) => a + (c.responseCount || 0), 0),
      teamsCount: list.reduce((a, c) => a + c.teamsCount, 0) + extra.filter((c) => c.responseCount).length,
      headcount: list.reduce((a, c) => a + c.headcount, 0),
    };
  };

  // Regions
  const regionNodes = {};
  for (const s of siteList) {
    const key = s.region || 'Other';
    (regionNodes[key] = regionNodes[key] || { id: key, type: 'region', name: key, sites: [] }).sites.push(s);
  }
  for (const r of Object.values(regionNodes)) {
    Object.assign(r, rollSites(r.sites));
    r.sites = r.sites.map((s) => s.id);
    finishNode(r);
  }

  // Enterprise
  const extra = unassigned.length ? [{ scores: meanOfResponses(unassigned), weight: unassigned.length, responseCount: unassigned.length }] : [];
  const enterprise = finishNode({ id: null, type: 'organization', name: org?.name || 'All Sites', ...rollSites(siteList, extra) });
  if (hc.organization > enterprise.headcount) enterprise.headcount = hc.organization;

  return { teams: teamNodes, departments: deptNodes, sites: siteNodes, regions: regionNodes, enterprise };
}

/** The rollup node for a snapshot scope ({ type, id }), or null when the scope is unknown. */
export function nodeForScope(rollup, scope) {
  if (!rollup) return null;
  if (!scope || !scope.type || scope.type === 'organization') return rollup.enterprise;
  if (scope.type === 'site') return rollup.sites[scope.id] || null;
  if (scope.type === 'department') return rollup.departments[scope.id] || null;
  if (scope.type === 'team') return rollup.teams[scope.id] || null;
  return null;
}

/** Headcount for a scope from the org's membersIndex (team → department → site → org). */
export function headcountForScope(org, scope) {
  const hc = headcountsFromMembers(org);
  if (!scope || !scope.type || scope.type === 'organization') return hc.organization;
  const teams = org?.teams || [];
  const departments = org?.departments || [];
  const deptTotal = (id) => (hc.department[id] || 0) + teams.filter((t) => t.departmentId === id).reduce((a, t) => a + (hc.team[t.id] || 0), 0);
  if (scope.type === 'team') return hc.team[scope.id] || 0;
  if (scope.type === 'department') return deptTotal(scope.id);
  if (scope.type === 'site') return (hc.site[scope.id] || 0) + departments.filter((d) => d.siteId === scope.id).reduce((a, d) => a + deptTotal(d.id), 0);
  return 0;
}
